import { serializeToPlainText } from '@enview/interface/utils/RichTextAnnotation';
import { Activity, ActivityType, UserActivity } from './Activity';
import { UserAction } from '@enview/interface/types/actions/UserAction';
import { PositionStatement } from '@enview/interface/types/bills/PositionStatement';
import { OrganizationUser } from '@enview/interface/types/OrganizationUser';
import { Organization } from '@enview/interface/types/Organization';
import { BillCustomField } from '@enview/interface/types/CustomFields';

// Activity Filters
type ActivityFilters = {
  [key: string]: (activity: Activity) => boolean;
};

export enum Filter {
  BILL_ACTIONS = 'Bill Actions',
  NOTES = 'Notes',
  VOTE_HISTORY = 'Vote History',
  HEARINGS = 'Hearings',
  FILES = 'Files',
  STARRED = 'Starred',
  ARCHIVED = 'Archived',
}

export const FILTER_OPTIONS = Object.values<string>(Filter).map((value) => {
  return { value, label: value };
});

const activityFilters: ActivityFilters = {
  [Filter.BILL_ACTIONS]: (activity: Activity) =>
    activity.type === ActivityType.BILL_ACTION,
  [Filter.NOTES]: (activity: Activity) => {
    const { userAction } = activity as UserActivity;
    const isEmptyText =
      userAction &&
      userAction.commentJson &&
      serializeToPlainText(userAction.commentJson) === '';
    return (
      activity.type === ActivityType.USER_ACTION &&
      !userAction.isArchived &&
      ((userAction.comment !== undefined &&
        userAction.comment !== null &&
        userAction.comment.length > 0) ||
        (userAction.commentJson !== undefined &&
          userAction.commentJson !== null &&
          !isEmptyText))
    );
  },
  [Filter.VOTE_HISTORY]: (activity: Activity) => {
    return activity.type === ActivityType.BILL_VOTE;
  },
  [Filter.HEARINGS]: (activity: Activity) => {
    return activity.type === ActivityType.BILL_HEARING;
  },
  [Filter.FILES]: (activity: Activity) => {
    return (
      activity.type === ActivityType.USER_ACTION &&
      (activity as UserActivity).userAction.userFiles !== undefined &&
      (activity as UserActivity).userAction.userFiles.length > 0 &&
      !(activity as UserActivity).userAction.isArchived
    );
  },
  [Filter.STARRED]: (activity: Activity) => {
    return (
      activity.type === ActivityType.USER_ACTION &&
      (activity as UserActivity).isStarred &&
      !(activity as UserActivity).userAction.isArchived
    );
  },
  [Filter.ARCHIVED]: (activity: Activity) => {
    return (
      activity.type === ActivityType.USER_ACTION &&
      (activity as UserActivity).isArchived
    );
  },
};

const isValidActivityFilterKey = (key: string): boolean => key in activityFilters;

export const filterActivities = (
  activities: Activity[],
  selectedFilters: string[],
): Activity[] => {
  // add on additional filters
  if (selectedFilters.length === 0) return activities;
  const finalFilteredActivities = selectedFilters.reduce(
    (remainingActivities: Activity[], filterValue: string) => {
      if (isValidActivityFilterKey(filterValue)) {
        const filteredActivities = activities.filter((activity) =>
          activityFilters[filterValue](activity),
        );
        remainingActivities.push(...filteredActivities);
      }
      return remainingActivities;
    },
    [],
  );

  // remove any duplicates
  return finalFilteredActivities.filter((activity, index) => {
    return finalFilteredActivities.indexOf(activity) === index;
  });
};

export const convertPositionStatementToUserAction = (
  positionStatement: PositionStatement,
): UserAction => {
  return {
    id: positionStatement.id,
    billId: positionStatement.billId,
    createdAt: positionStatement.createdAt.toString(),
    updatedAt: positionStatement.updatedAt.toString(),
    organizationUser: {} as OrganizationUser,
    organizationId: positionStatement.orgId,
    organizationName: '',
    organization: {} as Organization,
    sharedToTeamDiscussions: [],
    teams: [],
    isStarred: false,
    isArchived: positionStatement.isArchived || false,
    comment: positionStatement.positionStatement || '',
    commentJson: positionStatement.statementJson,
    userFileIds: [],
    userFiles: [],
    billData: {
      billId: positionStatement.billId,
    },
    mentionUserIds: [],
  };
};

export const convertCustomFieldToUserAction = (
  fieldValue: Partial<BillCustomField>,
): UserAction | null => {
  if (!fieldValue.id || !fieldValue.billId || !fieldValue?.fieldValue?.richText)
    return null;
  return {
    id: fieldValue.id,
    billId: fieldValue.billId,
    createdAt: (fieldValue?.createdAt ?? Date.now()).toString(),
    updatedAt: (fieldValue?.updatedAt ?? Date.now()).toString(),
    organizationUser: fieldValue?.updatedBy as OrganizationUser,
    organizationId: 0,
    organizationName: '',
    organization: {} as Organization,
    sharedToTeamDiscussions: [],
    teams: [],
    isStarred: false,
    isArchived: fieldValue?.isArchived || false,
    comment: '',
    commentJson: fieldValue?.fieldValue?.richText,
    userFileIds: [],
    userFiles: [],
    mentionUserIds: [],
  };
};
